// deck_busy: one saved-deck write, one absorbed lock loss.
//
// Spec amendment 2. Clients write `saved_decks` through PostgREST, so the
// Watchtower publish transaction protects itself with a `lock_timeout = 3s`
// pre-request hook rather than anything on our side. A write that loses that
// race comes back as Postgres 55P03 in the error BODY (never the HTTP status,
// which is a plain 500), and it is RETRYABLE: the publish is seconds long and
// the user's edit is still perfectly valid.
//
// So the contract is: keep the edit, stay quiet, retry once after 5s, and only
// then let the caller show an error. Nothing here decides what "quiet" looks
// like - that is the caller's `onBusy`.

import { deckBusy } from './watchtower'
import { tc } from '../i18n'

/** The publish holds its lock for 3s; 5s clears it with room to spare. */
export const DECK_BUSY_RETRY_MS = 5000

export type DeckWriteOutcome<T> =
  | { ok: true; value: T; retried: boolean }
  | { ok: false; error: unknown; retried: true }

export interface DeckBusyRetryOptions<T> {
  /**
   * True when a RESOLVED value carries a deck_busy error. supabase-js resolves
   * `{ data, error }` rather than rejecting, so this is the case that actually
   * happens in production; the rejection path below is for transports that
   * throw.
   */
  isBusyValue?: (value: T) => boolean
  /** Called once, before the wait, when a retry is about to happen. */
  onBusy?: () => void
  delayMs?: number
  /** Injectable for tests; the default is a plain timer. */
  sleep?: (ms: number) => Promise<void>
}

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * Run a saved-deck write, absorbing exactly ONE deck_busy loss.
 *
 * Non-busy failures are left exactly as they are today: a resolved error value
 * comes straight back for the caller's own handling, and a rejection is
 * rethrown. Only 55P03 changes anything.
 *
 * After a retry the outcome is whatever the second attempt produced, including
 * a second busy - that one IS the caller's error to surface, once.
 */
export async function withDeckBusyRetry<T>(
  attempt: () => PromiseLike<T>,
  opts: DeckBusyRetryOptions<T> = {},
): Promise<DeckWriteOutcome<T>> {
  const sleep = opts.sleep ?? defaultSleep
  let first: T
  try {
    first = await attempt()
    if (!opts.isBusyValue?.(first)) return { ok: true, value: first, retried: false }
  } catch (err) {
    // A rejection that is not a lock loss keeps today's behaviour exactly:
    // straight back out to the caller.
    if (!deckBusy(err)) throw err
  }

  opts.onBusy?.()
  await sleep(opts.delayMs ?? DECK_BUSY_RETRY_MS)

  try {
    return { ok: true, value: await attempt(), retried: true }
  } catch (err) {
    return { ok: false, error: err, retried: true }
  }
}

/** The error shape supabase-js puts in the `error` slot of its envelope. */
export interface DeckWriteError {
  message?: string
  code?: string
}

/**
 * Normalize a rejection into the `{ data, error }` envelope every caller in
 * useSavedDecks already knows how to read.
 *
 * Only reached after a retry we would not have attempted at all if the first
 * failure had not been a lock loss, so this cannot change the shape of any
 * error path that exists today.
 */
export function rejectionAsError(err: unknown): DeckWriteError {
  // The fallback is shown in the sync banner, so it is in the active language
  // (never sent to analytics or stored).
  if (err && typeof err === 'object') {
    const e = err as DeckWriteError
    return { message: e.message ?? tc('saved_decks.busy'), code: e.code }
  }
  return { message: typeof err === 'string' ? err : tc('saved_decks.busy') }
}
